import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Share,
  People,
  Schedule,
  ArrowBack,
  Edit as EditIcon,
  Facebook,
  Twitter,
  WhatsApp
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useForm, Controller } from 'react-hook-form';
import { 
  getPetitionById, 
  signPetition, 
  checkIfUserSigned,
  getPetitionSignatures 
} from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const PetitionDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [petition, setPetition] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signDialog, setSignDialog] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  const [signing, setSigning] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [loadingSignatures, setLoadingSignatures] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      message: '',
      is_anonymous: false
    }
  });

  const isAnonymous = watch('is_anonymous');

  useEffect(() => {
    fetchPetition();
  }, [id]);

  const fetchPetition = async () => {
    try {
      const data = await getPetitionById(id);
      setPetition(data);
      fetchSignatures();
    } catch (error) {
      toast.error('Petiția nu a fost găsită');
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatures = async () => {
    setLoadingSignatures(true);
    try {
      const data = await getPetitionSignatures(id);
      setSignatures(data);
    } catch (error) {
      console.error('Error fetching signatures:', error);
    } finally {
      setLoadingSignatures(false);
    }
  };

  const checkUserSignature = async (email) => {
    try {
      const signature = await checkIfUserSigned(id, email);
      setHasSigned(!!signature);
    } catch (error) {
      console.error('Error checking signature:', error);
    }
  };

  const onSubmit = async (data) => {
    setSigning(true);
    try {
      // Verifică dacă a semnat deja (doar pentru semnături non-anonime)
      if (!data.is_anonymous) {
        await checkUserSignature(data.email);
        if (hasSigned) {
          toast.error('Ai semnat deja această petiție');
          return;
        }
      }

      await signPetition({
        petition_id: id,
        name: data.name,
        email: data.is_anonymous ? 'anonymous@psde.md' : data.email,
        message: data.message || null,
        is_anonymous: data.is_anonymous
      });

      toast.success('Petiția a fost semnată cu succes!');
      setSignDialog(false);
      reset();
      fetchPetition(); // Refresh pentru a actualiza numărul de semnături
      fetchSignatures();
    } catch (error) {
      if (error.message?.includes('duplicate')) {
        toast.error('Ai semnat deja această petiție');
      } else {
        toast.error('Eroare la semnarea petiției');
      }
    } finally {
      setSigning(false);
    }
  };

  const shareUrl = window.location.href;
  const shareText = `Semnează această petiție importantă: ${petition?.title}`;

  const handleShare = (platform) => {
    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      default:
        return;
    }
    window.open(url, '_blank');
    setShareDialog(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!petition) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Petiția nu a fost găsită sau nu este disponibilă.
        </Alert>
      </Container>
    );
  }

  const signatureCount = petition.signatures?.[0]?.count || 0;
  const progressPercentage = petition.target_signatures 
    ? Math.min(100, (signatureCount / petition.target_signatures) * 100)
    : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        component={Link}
        to="/"
        sx={{ mb: 3 }}
      >
        Înapoi la Lista de Petiții
      </Button>

      <Grid container spacing={4}>
        {/* Conținutul principal */}
        <Grid item xs={12} lg={8}>
          <Card elevation={4}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 3 }}>
                {petition.category && (
                  <Chip 
                    label={petition.category} 
                    color="secondary" 
                    sx={{ mb: 2 }} 
                  />
                )}
                <Typography variant="h3" component="h1" gutterBottom>
                  {petition.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Schedule fontSize="small" color="text.secondary" />
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(petition.created_at), 'dd MMMM yyyy', { locale: ro })}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatDistanceToNow(new Date(petition.created_at), { 
                      addSuffix: true, 
                      locale: ro 
                    })}
                  </Typography>
                </Box>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h5" color="primary">
                    {signatureCount.toLocaleString()} semnături
                  </Typography>
                  {petition.target_signatures && (
                    <Typography variant="body2" color="text.secondary">
                      Obiectiv: {petition.target_signatures.toLocaleString()}
                    </Typography>
                  )}
                </Box>
                {petition.target_signatures && (
                  <LinearProgress 
                    variant="determinate" 
                    value={progressPercentage}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Descrierea scurtă */}
              <Typography variant="h6" gutterBottom color="primary">
                Pe scurt
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                {petition.description}
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Conținutul complet */}
              <Typography variant="h6" gutterBottom color="primary">
                Detalii complete
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: '1rem', 
                  lineHeight: 1.8,
                  whiteSpace: 'pre-wrap'
                }}
              >
                {petition.content}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Acțiuni */}
          <Card elevation={4} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sprijină această cauză
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => setSignDialog(true)}
                sx={{ mb: 2, py: 1.5 }}
                disabled={!petition.is_active}
              >
                <EditIcon sx={{ mr: 1 }} />
                Semnează Petiția
              </Button>
              
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Share />}
                onClick={() => setShareDialog(true)}
              >
                Distribuie
              </Button>

              {!petition.is_active && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Această petiție nu mai este activă
                </Alert>
              )}

              {user && (
                <Button
                  variant="text"
                  size="small"
                  component={Link}
                  to={`/admin/edit/${petition.id}`}
                  sx={{ mt: 1 }}
                  fullWidth
                >
                  Editează (Admin)
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Semnături recente */}
          <Card elevation={4}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Semnături recente
              </Typography>
              {loadingSignatures ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : signatures.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Fii primul care semnează!
                </Typography>
              ) : (
                <List sx={{ py: 0 }}>
                  {signatures.slice(0, 5).map((signature, index) => (
                    <ListItem key={signature.id} sx={{ px: 0 }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.main' }}>
                        {signature.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <ListItemText
                        primary={signature.name}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {formatDistanceToNow(new Date(signature.created_at), { 
                                addSuffix: true, 
                                locale: ro 
                              })}
                            </Typography>
                            {signature.message && (
                              <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                "{signature.message}"
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog pentru semnare */}
      <Dialog open={signDialog} onClose={() => setSignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Semnează această petiție</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Numele este obligatoriu' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Numele complet"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      disabled={signing}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="is_anonymous"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          disabled={signing}
                        />
                      }
                      label="Semnez anonim (email-ul nu va fi stocat)"
                    />
                  )}
                />
              </Grid>
              
              {!isAnonymous && (
                <Grid item xs={12}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{ 
                      required: !isAnonymous ? 'Email-ul este obligatoriu' : false,
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Email invalid'
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="email"
                        label="Adresa de email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        disabled={signing}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Controller
                  name="message"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={3}
                      label="Mesaj (opțional)"
                      placeholder="De ce susții această petiție?"
                      disabled={signing}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSignDialog(false)} disabled={signing}>
              Anulează
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={signing}
              startIcon={signing && <CircularProgress size={16} />}
            >
              {signing ? 'Se semnează...' : 'Semnează'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog pentru share */}
      <Dialog open={shareDialog} onClose={() => setShareDialog(false)}>
        <DialogTitle>Distribuie această petiție</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Ajută-ne să răspândim cuvântul despre această cauză importantă:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Facebook />}
              onClick={() => handleShare('facebook')}
              sx={{ color: '#1877f2', borderColor: '#1877f2' }}
            >
              Facebook
            </Button>
            <Button
              variant="outlined"
              startIcon={<Twitter />}
              onClick={() => handleShare('twitter')}
              sx={{ color: '#1da1f2', borderColor: '#1da1f2' }}
            >
              Twitter
            </Button>
            <Button
              variant="outlined"
              startIcon={<WhatsApp />}
              onClick={() => handleShare('whatsapp')}
              sx={{ color: '#25d366', borderColor: '#25d366' }}
            >
              WhatsApp
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialog(false)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PetitionDetailPage;
